import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, ShieldCheck, IdCard, FileText, MessageSquareText, Check, PenLine, Plane } from "lucide-react";

const SRC_STYLE = {
  verified: "bg-emerald-50 text-emerald-600", user_reported: "bg-cobalt-light text-cobalt",
  ai_extracted: "bg-purple-50 text-purple-700", unverified: "bg-amber-50 text-amber-700",
};
const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-cobalt";
const fmt = (s) => String(s).replace(/_/g, " ");

// Module-scope so inputs never remount mid-typing (focus preservation).
const Review = ({ sk, icon: Icon, title, checklist, results, s, upd, save, busy, decided, storylineQuestions }) => (
  <div className="rounded-2xl border border-slate-100 p-4" data-testid={`rnv-section-${sk}`}>
    <div className="flex items-center justify-between mb-2">
      <span className="font-semibold text-navy text-sm flex items-center gap-2">
        <Icon className="w-4 h-4 text-cobalt" /> {title}
        {s.result && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 capitalize">{fmt(s.result)}</span>}
      </span>
      {!decided && <button onClick={() => save(sk)} disabled={busy === sk} data-testid={`rnv-save-${sk}`}
        className="text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-200 text-cobalt hover:bg-cobalt-light disabled:opacity-50">
        {busy === sk ? "Saving…" : "Save review"}</button>}
    </div>
    {checklist && (
      <div className="grid sm:grid-cols-2 gap-1">
        {checklist.map((it) => (
          <label key={it.key} className="flex items-center gap-2 text-xs text-slate-600 py-1">
            <input type="checkbox" disabled={decided} checked={!!(s.checklist || {})[it.key]} data-testid={`rnv-${sk}-${it.key}`}
              onChange={(e) => upd(sk, { checklist: { ...(s.checklist || {}), [it.key]: e.target.checked } })}
              className="h-3.5 w-3.5 accent-cobalt" /> {it.label}
          </label>
        ))}
      </div>
    )}
    {sk === "storyline" && (
      <div className="grid sm:grid-cols-2 gap-2 mt-1">
        {storylineQuestions.map((q) => (
          <label key={q.key} className="block text-xs text-slate-500">{q.label}
            {q.type === "bool" ? (
              <select disabled={decided} value={(s.answers || {})[q.key] || ""} data-testid={`rnv-story-${q.key}`}
                onChange={(e) => upd(sk, { answers: { ...(s.answers || {}), [q.key]: e.target.value } })} className={`${inputCls} mt-0.5`}>
                <option value="">—</option><option value="yes">Yes</option><option value="no">No</option><option value="unknown">Unknown</option>
              </select>
            ) : (
              <input disabled={decided} value={(s.answers || {})[q.key] || ""} data-testid={`rnv-story-${q.key}`}
                onChange={(e) => upd(sk, { answers: { ...(s.answers || {}), [q.key]: e.target.value } })} className={`${inputCls} mt-0.5`} />
            )}
          </label>
        ))}
      </div>
    )}
    <div className="mt-2 flex flex-col sm:flex-row gap-2">
      <select disabled={decided} value={s.result || ""} data-testid={`rnv-result-${sk}`}
        onChange={(e) => upd(sk, { result: e.target.value })} className={inputCls}>
        <option value="">Select result…</option>
        {results.map((r) => <option key={r} value={r}>{fmt(r)}</option>)}
      </select>
      <input disabled={decided} value={s.notes || ""} onChange={(e) => upd(sk, { notes: e.target.value })}
        placeholder="Notes / documented mismatches" data-testid={`rnv-notes-${sk}`} className={inputCls} />
    </div>
  </div>
);

// Phase 3: RN verification duties — structured case screen, identity/insurance/
// storyline reviews, and two SEPARATE signed decisions (case + drone).
export default function RnVerificationPanel({ intakeId, onDone }) {
  const [d, setD] = useState(null);
  const [sections, setSections] = useState({ identity: {}, insurance: {}, storyline: {} });
  const [caseDecision, setCaseDecision] = useState("");
  const [droneDecision, setDroneDecision] = useState("");
  const [notes, setNotes] = useState("");
  const [sig, setSig] = useState("");
  const [c1, setC1] = useState(false);
  const [c2, setC2] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = () => api.get(`/nurse/intakes/${intakeId}/case-screen`).then(({ data }) => {
    setD(data);
    setSections({
      identity: data.rn_verification.identity || {},
      insurance: data.rn_verification.insurance || {},
      storyline: data.rn_verification.storyline || {},
    });
  }).catch(() => {});
  useEffect(() => { load(); }, [intakeId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!d) return <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  const decided = !!d.decisions;

  const upd = (sk, patch) => setSections({ ...sections, [sk]: { ...sections[sk], ...patch } });
  const save = async (sk) => {
    const s = sections[sk];
    if (!s.result) { toast.error("Select a result for this review."); return; }
    setBusy(sk);
    try {
      await api.put(`/nurse/intakes/${intakeId}/verification`, {
        section: sk, checklist: s.checklist || {}, answers: s.answers || {}, result: s.result, notes: s.notes || "",
      });
      toast.success(`RN ${sk} review saved`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed"); }
    finally { setBusy(null); }
  };

  const submit = async () => {
    if (!caseDecision || !droneDecision) { toast.error("Select both decisions — they are separate."); return; }
    if (!c1 || !c2) { toast.error("Confirm the review checkboxes."); return; }
    if (sig.trim().length < 2) { toast.error("Electronic signature required."); return; }
    setBusy("submit");
    try {
      const { data } = await api.post(`/nurse/intakes/${intakeId}/decisions`, {
        case_decision: caseDecision, drone_decision: droneDecision, notes,
        signature: sig.trim(), confirm_reviewed: c1, confirm_checklist: c2,
      });
      toast.success(`Decisions recorded — case: ${fmt(data.case_decision)}, drone: ${fmt(data.drone_decision)}`);
      load(); onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Submit failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="rn-verification-panel">
      <div className="rounded-2xl bg-navy text-white p-4">
        <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-cyan-300 flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> RN Case Verification</div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {d.fields.map((f) => (
            <span key={f.label} className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-2.5 py-1 text-[11px]">
              <span className="text-slate-300">{f.label}:</span> <b className="text-white">{String(f.value)}</b>
              <span className={`px-1.5 rounded-full text-[9px] font-bold uppercase ${SRC_STYLE[f.source]}`}>{fmt(f.source)}</span>
            </span>
          ))}
        </div>
        {d.flags.on_hold && <div className="mt-2 text-xs text-amber-300">⚠ Case on hold: {d.flags.hold_reasons.join(" · ")}</div>}
      </div>

      <Review sk="identity" icon={IdCard} title="Identity verification review" checklist={d.identity_checklist} results={d.identity_results}
        s={sections.identity} upd={upd} save={save} busy={busy} decided={decided} />
      <Review sk="insurance" icon={FileText} title="Insurance verification review" checklist={d.insurance_checklist} results={d.insurance_results}
        s={sections.insurance} upd={upd} save={save} busy={busy} decided={decided} />
      <Review sk="storyline" icon={MessageSquareText} title="Accident storyline verification" checklist={null} results={d.storyline_results}
        s={sections.storyline} upd={upd} save={save} busy={busy} decided={decided} storylineQuestions={d.storyline_questions} />
      <p className="text-[11px] text-slate-400">The RN documents consistency and reported facts only — never legal fault, coverage confirmation, or forensic document authentication.</p>

      {decided ? (
        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4 text-sm text-emerald-700" data-testid="rnv-decided">
          <div className="flex items-center gap-2 font-semibold"><Check className="w-4 h-4" /> Decisions recorded (immutable)</div>
          <div className="mt-1 capitalize">Case: <b>{fmt(d.decisions.case_decision)}</b> · Drone: <b>{fmt(d.decisions.drone_decision)}</b></div>
          <div className="text-xs mt-1">Signed by {d.decisions.signature} · {new Date(d.decisions.decided_at).toLocaleString()}</div>
        </div>
      ) : (
        <div className="rounded-2xl border-2 border-navy/10 p-4 space-y-3" data-testid="rnv-decisions">
          <h4 className="font-heading font-semibold text-navy text-sm">Two separate decisions</h4>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block text-xs text-slate-500"><span className="flex items-center gap-1 font-semibold"><ShieldCheck className="w-3.5 h-3.5 text-cobalt" /> Decision 1 — Case Verification</span>
              <select value={caseDecision} onChange={(e) => setCaseDecision(e.target.value)} data-testid="rnv-case-decision" className={`${inputCls} mt-1`}>
                <option value="">Select…</option>
                {d.case_decisions.map((r) => <option key={r} value={r}>{fmt(r)}</option>)}
              </select></label>
            <label className="block text-xs text-slate-500"><span className="flex items-center gap-1 font-semibold"><Plane className="w-3.5 h-3.5 text-cobalt" /> Decision 2 — Drone Approval</span>
              <select value={droneDecision} onChange={(e) => setDroneDecision(e.target.value)} data-testid="rnv-drone-decision" className={`${inputCls} mt-1`}>
                <option value="">Select…</option>
                {d.drone_decisions.map((r) => <option key={r} value={r}>{fmt(r)}</option>)}
              </select></label>
          </div>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="rnv-decision-notes"
            placeholder="Notes — inconsistencies, escalation reasons, or task list for the user (one per line)" className={`${inputCls} min-h-[60px]`} />
          <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={c1} onChange={(e) => setC1(e.target.checked)} data-testid="rnv-confirm-reviewed" className="h-4 w-4 accent-cobalt" /> I confirm all required questions were reviewed.</label>
          <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={c2} onChange={(e) => setC2(e.target.checked)} data-testid="rnv-confirm-checklist" className="h-4 w-4 accent-cobalt" /> I confirm the verification checklist was completed.</label>
          <div className="flex gap-2">
            <input value={sig} onChange={(e) => setSig(e.target.value)} placeholder="Electronic signature (full name, RN)"
              data-testid="rnv-signature" className={inputCls} />
            <button onClick={submit} disabled={busy === "submit"} data-testid="rnv-submit-decisions"
              className="shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-full bg-navy text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">
              {busy === "submit" ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />} Sign & submit decisions
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
